import React from 'react';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';
import { makeStyles } from 'src/makeStyles';
import { Fade, CircularProgress, IconButton } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { colors, fontSize } from '../../constants/styleConstants';

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: 500,
    maxWidth: '90%',
    padding: 28,
    maxHeight: '95vh',
    borderRadius: 4,
    backgroundColor: colors.white,
    textAlign: 'center',
    '&:focus': {
      outline: 'none',
    },
  },
  icon: {
    height: 48,
    width: 48,
  },
  title: {
    fontSize: fontSize.h3,
    marginBottom: '1rem',
    marginTop: '1.3rem',
    wordBreak: 'break-word',
  },
  description: {
    color: colors.cancelButton,
    lineHeight: '24px',
    fontSize: fontSize.description,
  },
  severalButtonsBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonsBlock: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: 25,
    '& .MuiButtonBase-root': {
      '& + .MuiButtonBase-root': {
        marginLeft: 40,
      },
    },
  },
  closeButton: {
    alignSelf: 'end',
  },
});

const ActionStatusModal = ({
  className,
  children,
  isOpened,
  type,
  onClose,
  buttons,
  title,
  description,
  showCloseIcon,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Modal open={isOpened} className={classes.root} onClose={onClose}>
      <Fade in={isOpened}>
        <div className={cx(classes.paper, className)}>
          {showCloseIcon && (
            <div className={classes.closeButton}>
              <IconButton aria-label="close" onClick={() => onClose()}>
                <CloseIcon />
              </IconButton>
            </div>
          )}
          {type === 'warning' && <WarningIcon className={classes.icon} style={{ fill: '#ff8393' }} />}
          {type === 'success' && <CheckCircleIcon className={classes.icon} style={{ fill: '#7ed321' }} />}
          {type === 'info' && <InfoIcon className={classes.icon} style={{ fill: '#81c5ff' }} />}
          {type === 'progress' && <CircularProgress size="30px" />}
          <span className={classes.title}>{title}</span>
          {description && <span className={classes.description}>{description}</span>}
          {children}
          <div className={classes.buttonsBlock}>
            {buttons &&
              buttons.map((button, index) => React.cloneElement(button, { key: `${type}-modal-button-${index}` }))}
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

ActionStatusModal.propTypes = {
  children: PropTypes.node,
  isOpened: PropTypes.bool,
  type: PropTypes.oneOf(['info', 'warning', 'success', 'progress']),
  onClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  buttons: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.instanceOf(null)]),
  className: PropTypes.string,
  showCloseIcon: PropTypes.bool,
};

ActionStatusModal.defaultProps = {
  isOpened: false,
  type: 'info',
  buttons: null,
  title: '',
  showCloseIcon: false,
};

export default ActionStatusModal;
